import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import { useLegalGate } from "../contexts/LegalGateContext";
import {
  getDocumentContent,
  getDocumentTitle,
} from "../utilities/legalDocuments";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import "../styleSheets/terms.css";
import "../styleSheets/legalAcceptance.css";

function LegalAcceptance() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { signOut } = useUser();
  const { status, acceptLegal } = useLegalGate();

  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const languageCode = i18n.language;

  const termsTitle = useMemo(
    () => getDocumentTitle("termsOfService", languageCode),
    [languageCode],
  );
  const termsContent = useMemo(
    () => getDocumentContent("termsOfService", languageCode),
    [languageCode],
  );
  const privacyTitle = useMemo(
    () => getDocumentTitle("privacyPolicy", languageCode),
    [languageCode],
  );
  const privacyContent = useMemo(
    () => getDocumentContent("privacyPolicy", languageCode),
    [languageCode],
  );

  if (status === "loading") {
    return <BlockingPageLoader message={t("legal.gate.loadingMessage")} />;
  }

  const handleConfirm = async () => {
    if (!accepted || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await acceptLegal();
      navigate("/Dashboard", { replace: true });
    } catch (error) {
      setSubmitError(t("legal.gate.errorMessage"));
      setIsSubmitting(false);
    }
  };

  const handleSignOut = () => {
    void signOut();
  };

  return (
    <div className="legal-acceptance-page">
      <div className="legal-acceptance-page__header">
        <h1 className="legal-acceptance-page__title">
          {t("legal.gate.pageTitle")}
        </h1>
        <button
          type="button"
          className="legal-acceptance-page__sign-out"
          onClick={handleSignOut}
        >
          {t("legal.gate.signOut")}
        </button>
      </div>

      <section aria-labelledby="legal-acceptance-terms-title">
        <h2 id="legal-acceptance-terms-title">{termsTitle}</h2>
        <div className="legal-acceptance-page__document legal-page">
          <div dangerouslySetInnerHTML={{ __html: termsContent }} />
        </div>
      </section>

      <section aria-labelledby="legal-acceptance-privacy-title">
        <h2 id="legal-acceptance-privacy-title">{privacyTitle}</h2>
        <div className="legal-acceptance-page__document legal-page">
          <div dangerouslySetInnerHTML={{ __html: privacyContent }} />
        </div>
      </section>

      <div className="legal-acceptance-page__checkbox-row">
        <input
          type="checkbox"
          id="legal-acceptance-checkbox"
          checked={accepted}
          onChange={(event) => setAccepted(event.target.checked)}
        />
        <label htmlFor="legal-acceptance-checkbox">
          {t("legal.gate.checkboxLabel")}
        </label>
      </div>

      {submitError && (
        <p className="legal-acceptance-page__error" role="alert">
          {submitError}
        </p>
      )}

      <button
        type="button"
        className="legal-acceptance-page__confirm-button"
        disabled={!accepted || isSubmitting}
        onClick={handleConfirm}
      >
        {isSubmitting
          ? t("legal.gate.submittingMessage")
          : t("legal.gate.confirmButton")}
      </button>
    </div>
  );
}

export default LegalAcceptance;
